import { useState, useEffect, useMemo } from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import { Box, Flex, Icon, Text } from "@storyofams/react-ui";
import { m } from "framer-motion";
import { useForm } from "react-hook-form";
import { FormattedMessage, useIntl } from "react-intl";
import { useHistory } from "react-router-dom";
import * as Yup from "yup";

import { Button, Link, Subtext, Input, Title } from "~/components";
import { ReactComponent as email } from "~/components/Icon/library/email.svg";
import config from "~/config";
import { convertCSStoObject } from "~/context/helper";
import { FlowContainerFragmentFragment } from "~/graphql/api/sdk";
import { useIsMobile, useIsRtl, useSession, useFlow } from "~/hooks";

import { replaceVariables } from "~/context/helper";

import { messages } from "./messages";
import QuestionContentBlocksList from "~/components/QuestionContentBlocksList";

import "react-phone-input-2/lib/style.css";
import PhoneInput from "react-phone-input-2";

const formatToE164 = (phone) => {
  const e164 = phone.replace(/(?!^\+)[^\d]/g, "");
  if (e164?.length < 8 || !e164.startsWith("+")) {
    return null;
  }
  return e164;
};

const isExactMatch = (ruleArray, answersArray) => {
  // true if all elements in ruleArray are in answersArray and vice versa
  return ruleArray.length === answersArray.length && ruleArray.every((value) => answersArray.includes(value));
};

const isOpenMatch = (ruleArray, answersArray) => {
  // true if all elements in ruleArray and in answersArray, but answersArray can have more elements
  return ruleArray.every((value) => answersArray.includes(value));
};

const isBroadMatch = (ruleArray, answersArray) => {
  // true if at least one element in ruleArray is in answersArray
  return ruleArray.some((value) => answersArray.includes(value));
};

const checkCondition = (condition, answers) => {
  // find an answer with a key matching the propertyId
  const answer = answers?.[condition.propertyId]?.trim()?.toLowerCase();

  if (condition?.match === "provided") {
    if (condition?.verb === "is" && answer) {
      return true;
    } else if (condition?.verb === "is_not" && !answer) {
      return true;
    } else if (condition?.verb === "is" && !answer) {
      return false;
    } else if (condition?.verb === "is_not" && answer) {
      return false;
    }
  }

  if (!condition?.verb || !condition?.propertyId || !condition?.propertyValue || !condition?.match) {
    return false;
  }

  if (!answer) {
    return condition?.verb === "is" ? false : true;
  }

  const answerArray = answer.split(",")?.map((item) => item.trim().toLowerCase());
  const conditionArray = condition?.propertyValue.split(",")?.map((item) => item.trim().toLowerCase());
  let isMatch = false;
  if (condition?.match === "exactly") {
    isMatch = isExactMatch(conditionArray, answerArray);
  } else if (condition?.match === "at_least") {
    isMatch = isOpenMatch(conditionArray, answerArray);
  } else if (condition?.match === "one_of") {
    isMatch = isBroadMatch(conditionArray, answerArray);
  }

  return condition?.verb === "is" ? isMatch : !isMatch;
};

const fadeOut = {
  animate: { opacity: 1 },
  exit: { opacity: 0 },
};

const appear = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
};

const MotionBox = m(Box);
const MotionFlex = m(Flex);

interface EmailFormProps {
  question: FlowContainerFragmentFragment["flow"]["nodes"][0];
  current: number;
}

export const EmailForm = ({ question, current }: EmailFormProps) => {
  const { endSession, addAnswer, session } = useSession();
  const history = useHistory();
  const intl = useIntl();
  const isMobile = useIsMobile();
  const isRtl = useIsRtl();
  const { flow, totalQuestions, hasWelcomeScreen } = useFlow();
  const [gdprAccepted, setGdprAccepted] = useState(false);
  const [advancedAnalyticsLoadSent, setAdvancedAnalyticsLoadSent] = useState(false);
  const [canDisplayQuestion, setCanDisplayQuestion] = useState(false);

  // if email is available as a URL parameter, prefill the email field
  const defaultEmail = new URLSearchParams(window.location.search).get("email");
  const defaultPhone = new URLSearchParams(window.location.search).get("phone");

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (session?.id && question?.id && !advancedAnalyticsLoadSent) {
      /* advanced analytics */
      // load_email_page
      if ((window as any)?.lantern_analytics_event) {
        (window as any).lantern_analytics_event({
          event: "load_email_page",
          page: "email_page",
          session_id: session.id,
          question_id: question.id,
        });
      }
      if ((window as any)?.lantern_gtag) {
        (window as any).lantern_gtag({
          event: "load_email_page",
          page: "email_page",
          session_id: session.id,
          question_id: question.id,
        });
      }
      setAdvancedAnalyticsLoadSent(true);
      /**********************/
    }
  }, [session, question]);

  useEffect(() => {
    if (!session || !question) {
      return;
    }

    const answers = session?.answers ? JSON.parse(session?.answers) : {};
    const pageSettings = JSON.parse(question?.pageSettings ?? "{}");
    const condition_settings = pageSettings?.conditions ? JSON.parse(pageSettings.conditions) : {};
    const must_match_all = condition_settings?.anyAll === "all";
    const should_skip = condition_settings?.showSkip === "skip";
    const conditions = condition_settings?.conditions ?? [];

    if (!conditions || conditions.length === 0) {
      // if no conditions are set, display the question
      setCanDisplayQuestion(true);
      return;
    }

    let conditionsMet = false;
    if (must_match_all) {
      // need to meet all conditions
      conditionsMet = conditions.every((condition) => {
        return checkCondition(condition, answers);
      });
    } else {
      // need to meet at least one condition
      conditionsMet = conditions.some((condition) => {
        return checkCondition(condition, answers);
      });
    }

    const skip = (conditionsMet && should_skip) || (!conditionsMet && !should_skip);

    if (skip) {
      // if this is from the back button, go back even more
      if (history?.action === "POP") {
        history.goBack();
      } else {
        // skip the question
        if (current >= totalQuestions) {
          openResults();
        } else {
          history.push(`/questions/${current + 1}`);
        }
      }
    } else {
      // if the question is not skipped, display it
      setCanDisplayQuestion(true);
    }
  }, [question, session]);

  useEffect(() => {
    if (question?.id) {
      // emit event lantern:display_question
      const displayQuestionEvent = new CustomEvent("lantern:display_question", {
        bubbles: true,
        cancelable: true,
        composed: false,
        detail: {
          id: question.id,
          title: question.title,
          description: question.description,
          type: question.type,
          options: question.options,
          isRequired: question.isRequired,
        },
      });
      window.dispatchEvent(displayQuestionEvent);
    }
  }, [question]);

  useEffect(() => {
    const sendHeightToParent = () => {
      const height = document.body.scrollHeight;
      if (height) {
        window?.parent?.postMessage(
          {
            action: "resize",
            frameHeight: height,
          },
          "*"
        );
      }
    };

    const images = document.querySelectorAll("img");
    images.forEach((image) => {
      if (image.complete) {
        sendHeightToParent();
      } else {
        image.addEventListener("load", sendHeightToParent);
        image.addEventListener("error", sendHeightToParent);
      }
    });
    window.addEventListener("load", sendHeightToParent);

    sendHeightToParent();

    const resizeObserver = new ResizeObserver(sendHeightToParent);
    resizeObserver.observe(document.body);

    return () => {
      resizeObserver.disconnect();
      images.forEach((image) => {
        image.removeEventListener("load", sendHeightToParent);
        image.removeEventListener("error", sendHeightToParent);
        window.removeEventListener("load", sendHeightToParent);
      });
    };
  }, []);

  const [isBusy, setBusy] = useState(false);

  const pageSettings = JSON.parse(flow?.pageSettings ?? "{}");
  const emailPage_buttonColor = pageSettings?.emailPage_buttonColor;
  const emailPage_useDefaultColorForButton = pageSettings?.emailPage_useDefaultColorForButton ?? "yes";
  const emailPage_buttonTextColor = pageSettings?.emailPage_buttonTextColor;
  const emailPage_useDefaultTextColorForButton = pageSettings?.emailPage_useDefaultTextColorForButton ?? "yes";
  const emailPage_displayHeadlineSubheadline = pageSettings?.emailPage_displayHeadlineSubheadline ?? "yes";
  const emailPage_displayEmailIcon = pageSettings?.emailPage_displayEmailIcon ?? "yes";

  const emailPage_TopWidthType = pageSettings?.emailPage_TopWidthType ?? "100%";
  const emailPage_BottomWidthType = pageSettings?.emailPage_BottomWidthType ?? "custom";
  const emailPage_TopWidth = pageSettings?.emailPage_TopWidth ?? 480;
  const emailPage_BottomWidth = pageSettings?.emailPage_BottomWidth ?? 370;

  const emailPage_TopWidthMobileType = pageSettings?.emailPage_TopWidthMobileType ?? "100%";
  const emailPage_BottomWidthMobileType = pageSettings?.emailPage_BottomWidthMobileType ?? "100%";
  const emailPage_TopWidthMobile = pageSettings?.emailPage_TopWidthMobile ?? "100%";
  const emailPage_BottomWidthMobile = pageSettings?.emailPage_BottomWidthMobile ?? "100%";

  const emailPage_buttonTextOverride = pageSettings?.emailPage_buttonTextOverride;
  const emailPage_buttonFontSize = pageSettings?.emailPage_buttonFontSize;
  const emailPage_buttonBorderRadius = pageSettings?.emailPage_buttonBorderRadius;
  const emailPage_buttonHorizontalPadding = pageSettings?.emailPage_buttonHorizontalPadding;
  const emailPage_buttonVerticalPadding = pageSettings?.emailPage_buttonVerticalPadding;
  const emailPage_buttonWidthType = pageSettings?.emailPage_buttonWidthType ?? "100%";
  const emailPage_buttonWidth = pageSettings?.emailPage_buttonWidth ?? "100%";

  const emailPage_buttonFontSizeMobile = pageSettings?.emailPage_buttonFontSizeMobile;
  const emailPage_buttonBorderRadiusMobile = pageSettings?.emailPage_buttonBorderRadiusMobile;
  const emailPage_buttonHorizontalPaddingMobile = pageSettings?.emailPage_buttonHorizontalPaddingMobile;
  const emailPage_buttonVerticalPaddingMobile = pageSettings?.emailPage_buttonVerticalPaddingMobile;
  const emailPage_buttonWidthMobileType = pageSettings?.emailPage_buttonWidthMobileType ?? "100%";
  const emailPage_buttonWidthMobile = pageSettings?.emailPage_buttonWidthMobile ?? "100%";

  const emailPage_displayEmailInput = pageSettings?.emailPage_displayEmailInput ?? "yes";
  const emailPage_displayPhoneInput = pageSettings?.emailPage_displayPhoneInput ?? "no";
  const emailPage_phoneLabel = pageSettings?.emailPage_phoneLabel;
  const emailPage_phonePlaceholder = pageSettings?.emailPage_phonePlaceholder;
  const emailPage_phoneDefaultCountry = pageSettings?.emailPage_phoneDefaultCountry;
  const emailPage_phoneOnlyCountries = pageSettings?.emailPage_phoneOnlyCountries;
  const emailPage_phoneExcludedCountries = pageSettings?.emailPage_phoneExcludedCountries;
  const emailPage_phonePreferredCountries = pageSettings?.emailPage_phonePreferredCountries;
  const emailPage_headlineFontSize = pageSettings?.emailPage_headlineFontSize ?? 24;
  const emailPage_subheadlineFontSize = pageSettings?.emailPage_subheadlineFontSize ?? 16;
  const emailPage_headlineAlignment = pageSettings?.emailPage_headlineAlignment ?? "center";
  const emailPage_subheadlineAlignment = pageSettings?.emailPage_subheadlineAlignment ?? "center";

  const emailPage_customMasks = pageSettings?.emailPage_customMasks ?? "{}";

  let customMasks = {};
  try {
    customMasks = JSON.parse(emailPage_customMasks);
  } catch (e) {
    customMasks = {};
  }

  const schema = Yup.object().shape({
    email: emailPage_displayEmailInput === "yes" ? Yup.string().email().required(intl.formatMessage(messages.emailRequired)) : Yup.string(),
    phone:
      emailPage_displayPhoneInput === "yes"
        ? Yup.string().matches(/.{8,}/g, intl.formatMessage(messages.phoneRequired)).required(intl.formatMessage(messages.phoneRequired))
        : Yup.string(),
    name: flow?.askForName === "required" ? Yup.string().required(intl.formatMessage(messages.nameRequired)) : Yup.string(),
  });

  const {
    formState: { isSubmitting, errors },
    handleSubmit,
    register,
    watch,
    setValue,
  } = useForm({
    defaultValues: {
      email: defaultEmail ?? "",
      phone: defaultPhone ?? "",
      name: "",
    },
    resolver: yupResolver(schema),
  });
  const phoneValue = watch("phone");
  const { ref: phoneRef, onBlur: phoneOnBlur, name: phoneName } = register("phone", { required: emailPage_displayPhoneInput === "yes" });

  const contentBlocks = useMemo(() => JSON.parse(question?.contentBlocks ?? "{}"), [question]);

  const sessionVariables = session?.answers ? JSON.parse(session?.answers) : {};

  const openResults = async (email = undefined) => {
    if (isBusy) {
      return;
    }

    setBusy(true);

    const success = email ? await endSession(email) : await endSession();

    if (success) {
      history.push("/results");
    } else {
      setBusy(false);
    }
  };

  const onSubmit = async (values) =>
    new Promise<void>(async (resolve) => {
      let marketingConsent = "NA";
      if (flow?.gdprCheckbox === "above" || flow?.gdprCheckbox === "below") {
        marketingConsent = "YES";
      } else if (flow?.gdprCheckbox === "above-optional" || flow?.gdprCheckbox === "below-optional") {
        marketingConsent = gdprAccepted ? "YES" : "NO";
      }

      // emit event lantern:submit_answer
      const submitAnswerEvent = new CustomEvent("lantern:submit_answer", {
        bubbles: true,
        cancelable: true,
        composed: false,
        detail: {
          id: question.id,
          title: question.title,
          description: question.description,
          type: question.type,
          options: question.options,
          isRequired: question.isRequired,
          answers: [],
          email: values.email,
          phone: formatToE164(values.phone),
          first_name: values.name,
          marketing_consent: marketingConsent,
        },
      });
      window.dispatchEvent(submitAnswerEvent);

      if (session?.id && question?.id) {
        /* advanced analytics */
        // submit_email
        if ((window as any)?.lantern_analytics_event) {
          (window as any).lantern_analytics_event({
            event: "submit_email",
            page: "email_page",
            session_id: session.id,
            question_id: question.id,
            email: values.email,
            phone: formatToE164(values.phone),
            first_name: values.name,
            marketing_consent: marketingConsent,
            is_required: question.isRequired,
          });
        }
        if ((window as any)?.lantern_gtag) {
          (window as any).lantern_gtag({
            event: "submit_email",
            page: "email_page",
            session_id: session.id,
            question_id: question.id,
            email: values.email,
            phone: formatToE164(values.phone),
            first_name: values.name,
            marketing_consent: marketingConsent,
            is_required: question.isRequired,
          });
        }
        // log email provided
        if ((window as any)?.lantern_analytics_event) {
          (window as any).lantern_analytics_event({
            event: "log_answer",
            page: "email_page",
            session_id: session.id,
            question_id: question.id,
            answers: "email provided",
            email: values.email,
            phone: formatToE164(values.phone),
            first_name: values.name,
            marketing_consent: marketingConsent,
            is_required: question.isRequired,
          });
        }
        /**********************/
      }

      await addAnswer({
        nodeId: question.id,
        propertyName: "MARKETING_CONSENT",
        propertyValue: marketingConsent,
      });

      if (values.email) {
        await addAnswer({
          nodeId: question.id,
          propertyName: "email",
          propertyValue: values.email,
        });
      }

      if (formatToE164(values.phone) !== null) {
        await addAnswer({
          nodeId: question.id,
          propertyName: "phone",
          propertyValue: formatToE164(values.phone),
        });
      }

      const result = await addAnswer({
        nodeId: question.id,
        propertyName: "SPECIALNAME",
        propertyValue: values.name,
      });

      if (!result) {
        resolve();
        return;
      }

      if ((current === totalQuestions && (!question?.nextNode || question?.nextNode === "auto")) || question?.nextNode === "results") {
        await openResults(values.email);
      } else {
        const result = await addAnswer({
          nodeId: question.id,
          propertyName: "email",
          propertyValue: values.email,
        });

        if (!result) {
          resolve();
          return;
        }

        if (!question?.nextNode || question?.nextNode === "auto") {
          history.push(`/questions/${current + 1}`);
        } else {
          const nextQuestionIdx = flow.nodes?.findIndex(({ id }) => id === question.nextNode);
          if (!nextQuestionIdx || nextQuestionIdx === -1) {
            history.push(`/questions/${current + 1}`);
          } else {
            history.push(`/questions/${nextQuestionIdx + (hasWelcomeScreen ? 0 : 1)}`);
          }
        }
      }
    });

  const skipOptin = async () => {
    if (session?.id && question?.id) {
      /* advanced analytics */
      // skip_optin
      if ((window as any)?.lantern_analytics_event) {
        (window as any).lantern_analytics_event({
          event: "skip_optin",
          page: "email_page",
          session_id: session.id,
          question_id: question.id,
        });
      }
      if ((window as any)?.lantern_gtag) {
        (window as any).lantern_gtag({
          event: "skip_optin",
          page: "email_page",
          session_id: session.id,
          question_id: question.id,
        });
      }
      // log skipped optin
      if ((window as any)?.lantern_analytics_event) {
        (window as any).lantern_analytics_event({
          event: "log_answer",
          page: "email_page",
          session_id: session.id,
          question_id: question.id,
          answers: "skipped optin",
        });
      }
      /**********************/
    }

    if ((current === totalQuestions && (!question?.nextNode || question?.nextNode === "auto")) || question?.nextNode === "results") {
      await openResults();
    } else {
      if (!question?.nextNode || question?.nextNode === "auto") {
        history.push(`/questions/${current + 1}`);
      } else {
        const nextQuestionIdx = flow.nodes?.findIndex(({ id }) => id === question.nextNode);
        if (!nextQuestionIdx || nextQuestionIdx === -1) {
          history.push(`/questions/${current + 1}`);
        } else {
          history.push(`/questions/${nextQuestionIdx + (hasWelcomeScreen ? 0 : 1)}`);
        }
      }
    }
  };

  const gdprTextParts = flow?.gdprText?.split(/<a>(.*?)<\/a>/);
  const gdprText1 = gdprTextParts?.[0] ?? "";
  const gdprLink1 = (
    <a target="_blank" href={flow?.gdprLink || ""} style={{ textDecoration: "underline", cursor: "pointer" }}>
      {gdprTextParts?.[1] ?? ""}
    </a>
  );
  const gdprText2 = gdprTextParts?.[2] ?? "";

  const gdprBlock = (
    <Flex className="gdpr-container" flexDirection="row" alignItems="center" justifyContent="center" mt={2} mb={2}>
      <label htmlFor="gdpr" className="gdpr-wrapper" style={{ fontSize: "12px", cursor: "pointer" }}>
        <input
          id="gdpr"
          type="checkbox"
          checked={gdprAccepted}
          onChange={(e) => setGdprAccepted(e.target.checked)}
          style={{
            borderRadius: "3px",
            verticalAlign: "-2px",
            transform: "scale(1.25)",
            marginRight: "3px",
            cursor: "pointer",
          }}
          className="gdpr-checkbox"
        />{" "}
        <span className="gdpr-text" style={{ fontSize: "12px" }}>
          {gdprText1}
          <span className="gdpr-link">{gdprLink1}</span>
          {gdprText2}
        </span>
      </label>
    </Flex>
  );

  if (!canDisplayQuestion) {
    return null;
  }

  if (isBusy) {
    return (
      <Box
        className="email-loading"
        width={{
          _: `${emailPage_TopWidthMobileType === "100%" ? "100%" : `${emailPage_TopWidthMobile}px`}`,
          lg: `${emailPage_TopWidthType === "100%" ? "100%" : `${emailPage_TopWidth}px`}`,
        }}
        mx="auto"
      >
        <Flex justifyContent="center" alignItems="center" height="50vh">
          <div className="loader"></div>
        </Flex>
      </Box>
    );
  }

  return (
    <form className="email-form" id={question?.id ?? ""} onSubmit={handleSubmit(onSubmit)}>
      <MotionBox transition={config.transition} initial="initial" animate="animate" exit="exit" variants={fadeOut} textAlign="center" mt={[7, 10]}>
        <Box
          className="email-top-section"
          width={{
            _: `${emailPage_TopWidthMobileType === "100%" ? "100%" : `${emailPage_TopWidthMobile}px`}`,
            lg: `${emailPage_TopWidthType === "100%" ? "100%" : `${emailPage_TopWidth}px`}`,
          }}
          mx="auto"
        >
          <m.div variants={appear} transition={config.transition}>
            {emailPage_displayEmailIcon !== "no" && (
              <Flex
                className="email-icon-container"
                justifyContent="center"
                alignItems="center"
                borderRadius="full"
                bg="white"
                width="80px"
                height="80px"
                mb={[4, 5]}
                mx="auto"
              >
                <Icon className="email-icon" icon={email} />
              </Flex>
            )}

            <QuestionContentBlocksList contentBlocks={contentBlocks?.top} sessionVariables={sessionVariables} />

            {emailPage_displayHeadlineSubheadline !== "no" && (
              <>
                <Title
                  className="email-heading"
                  role="heading"
                  aria-level={1}
                  tabIndex={1}
                  aria-live="assertive"
                  dir={isRtl ? "auto" : undefined}
                  style={{
                    fontSize: `${emailPage_headlineFontSize}px`,
                    textAlign: emailPage_headlineAlignment,
                    lineHeight: "1.2",
                    ...convertCSStoObject(flow?.cssEditor)[".email-heading"],
                  }}
                >
                  {replaceVariables(question?.title, sessionVariables) || intl.formatMessage(messages.emailTitle)}
                </Title>
                <Subtext
                  className="email-subheading"
                  mt={2}
                  mb={4}
                  dir={isRtl ? "auto" : undefined}
                  style={{
                    fontSize: `${emailPage_subheadlineFontSize}px`,
                    textAlign: emailPage_subheadlineAlignment,
                    lineHeight: "1.2",
                    ...convertCSStoObject(flow?.cssEditor)[".email-subheading"],
                  }}
                >
                  {replaceVariables(question?.description, sessionVariables) || intl.formatMessage(messages.emailSubtext)}
                </Subtext>
              </>
            )}
          </m.div>
        </Box>

        {emailPage_displayHeadlineSubheadline !== "no" && (
          <QuestionContentBlocksList contentBlocks={contentBlocks?.middle} sessionVariables={sessionVariables} />
        )}

        <Box
          className="email-bottom-section"
          width={{
            _: `${emailPage_BottomWidthMobileType === "100%" ? "100%" : `${emailPage_BottomWidthMobile}px`}`,
            lg: `${emailPage_BottomWidthType === "100%" ? "100%" : `${emailPage_BottomWidth}px`}`,
          }}
          mx="auto"
        >
          {(flow?.askForName === "required" || flow?.askForName === "optional") && (
            <m.div className="name-input-container" variants={appear} transition={{ delay: 0.2, ...config.transition }}>
              <Input
                autoFocus={!isMobile}
                label={intl.formatMessage(messages.nameLabel)}
                placeholder={intl.formatMessage(messages.namePlaceholder)}
                type="text"
                autocomplete="false"
                error={errors?.name?.message}
                mb={2}
                {...register("name", { required: flow?.askForName === "required" })}
              />
            </m.div>
          )}

          {emailPage_displayEmailInput !== "no" && (
            <m.div className="email-input-container" variants={appear} transition={{ delay: 0.2, ...config.transition }}>
              <Input
                autoFocus={!isMobile && !(flow?.askForName === "required" || flow?.askForName === "optional")}
                label={intl.formatMessage(messages.emailLabel)}
                placeholder={intl.formatMessage(messages.emailPlaceholder)}
                defaultValue={defaultEmail ?? ""}
                type="email"
                autocomplete="email"
                error={errors?.email?.message}
                mb={2}
                {...register("email", { required: emailPage_displayEmailInput === "yes" })}
              />
            </m.div>
          )}

          {emailPage_displayPhoneInput !== "no" && (
            <m.div className="phone-input-section" variants={appear} transition={{ delay: 0.2, ...config.transition }}>
              <div style={{ textAlign: "left", width: "100%" }}>
                {emailPage_phoneLabel && (
                  <Text
                    as="label"
                    htmlFor="phone"
                    fontSize="12px"
                    lineHeight="15px"
                    mb={1}
                    letterSpacing="-0.01em"
                    color="black90"
                    display="inline-block"
                    verticalAlign="top"
                  >
                    {emailPage_phoneLabel}
                  </Text>
                )}
                <PhoneInput
                  key={`${emailPage_phoneOnlyCountries}-${emailPage_phoneExcludedCountries}-${emailPage_phonePreferredCountries}-${emailPage_phoneDefaultCountry}`}
                  enableClickOutside={false}
                  masks={customMasks}
                  placeholder={emailPage_phonePlaceholder}
                  {...(emailPage_phoneDefaultCountry?.length > 0 && {
                    country: emailPage_phoneDefaultCountry?.trim()?.toLowerCase(),
                  })}
                  preferredCountries={emailPage_phonePreferredCountries?.split(",")?.map((country) => country.trim().toLowerCase()) ?? []}
                  excludeCountries={emailPage_phoneExcludedCountries?.split(",")?.map((country) => country.trim().toLowerCase()) ?? []}
                  {...(emailPage_phoneOnlyCountries?.length > 0 && {
                    onlyCountries: emailPage_phoneOnlyCountries.split(",").map((country) => country.trim().toLowerCase()),
                  })}
                  inputClass="phone-input"
                  containerClass="phone-input-container"
                  inputStyle={{
                    width: "100%",
                    height: "50px",
                    fontSize: "16px",
                    borderRadius: "8px",
                    border: "1px solid #e5e5e5",
                  }}
                  inputProps={{
                    name: phoneName,
                    ref: phoneRef,
                    onBlur: phoneOnBlur,
                    id: "phone",
                  }}
                  value={phoneValue}
                  onChange={(phone) => setValue("phone", phone, { shouldValidate: true })}
                />
                {errors?.phone && (
                  <Text
                    fontSize="12px"
                    lineHeight="15px"
                    mt={1}
                    letterSpacing={isRtl ? undefined : "-0.01em"}
                    color="errorText"
                    display="inline-block"
                    verticalAlign="top"
                    role="alert"
                  >
                    {errors.phone.message}
                  </Text>
                )}
              </div>
            </m.div>
          )}

          <QuestionContentBlocksList contentBlocks={contentBlocks?.bottom} sessionVariables={sessionVariables} />

          <MotionFlex
            className="email-button-container"
            mt={4}
            variants={appear}
            transition={{ delay: 0.3, ...config.transition }}
            flexDirection="column"
            alignItems="center"
          >
            {(flow?.gdprCheckbox === "above" || flow?.gdprCheckbox === "above-optional") && gdprBlock}

            <Button
              backgroundColor={emailPage_useDefaultColorForButton === "no" ? emailPage_buttonColor : undefined}
              textColor={emailPage_useDefaultTextColorForButton === "no" ? emailPage_buttonTextColor : undefined}
              style={
                isMobile
                  ? {
                      ...(emailPage_buttonFontSizeMobile >= 0 ? { fontSize: emailPage_buttonFontSizeMobile } : {}),
                      ...(emailPage_buttonBorderRadiusMobile >= 0 ? { borderRadius: emailPage_buttonBorderRadiusMobile } : {}),
                      ...(emailPage_buttonHorizontalPaddingMobile >= 0 ? { paddingLeft: emailPage_buttonHorizontalPaddingMobile } : {}),
                      ...(emailPage_buttonHorizontalPaddingMobile >= 0 ? { paddingRight: emailPage_buttonHorizontalPaddingMobile } : {}),
                      ...(emailPage_buttonVerticalPaddingMobile >= 0 ? { paddingTop: emailPage_buttonVerticalPaddingMobile } : {}),
                      ...(emailPage_buttonVerticalPaddingMobile >= 0 ? { paddingBottom: emailPage_buttonVerticalPaddingMobile } : {}),
                    }
                  : {
                      ...(emailPage_buttonFontSize >= 0 ? { fontSize: emailPage_buttonFontSize } : {}),
                      ...(emailPage_buttonBorderRadius >= 0 ? { borderRadius: emailPage_buttonBorderRadius } : {}),
                      ...(emailPage_buttonHorizontalPadding >= 0 ? { paddingLeft: emailPage_buttonHorizontalPadding } : {}),
                      ...(emailPage_buttonHorizontalPadding >= 0 ? { paddingRight: emailPage_buttonHorizontalPadding } : {}),
                      ...(emailPage_buttonVerticalPadding >= 0 ? { paddingTop: emailPage_buttonVerticalPadding } : {}),
                      ...(emailPage_buttonVerticalPadding >= 0 ? { paddingBottom: emailPage_buttonVerticalPadding } : {}),
                    }
              }
              disabled={(flow?.gdprCheckbox === "above" || flow?.gdprCheckbox === "below") && !gdprAccepted}
              className="email-submit-button"
              arrow
              type="submit"
              isLoading={isSubmitting}
              width={
                isMobile
                  ? emailPage_buttonWidthMobileType === "100%"
                    ? "100%"
                    : emailPage_buttonWidthMobileType === "auto"
                    ? "auto"
                    : `${emailPage_buttonWidthMobile}px`
                  : emailPage_buttonWidthType === "100%"
                  ? "100%"
                  : emailPage_buttonWidthType === "auto"
                  ? "auto"
                  : `${emailPage_buttonWidth}px`
              }
            >
              {emailPage_buttonTextOverride?.length > 0 ? emailPage_buttonTextOverride : <FormattedMessage {...messages.submit} />}
            </Button>

            {(flow?.gdprCheckbox === "below" || flow?.gdprCheckbox === "below-optional") && gdprBlock}

            {!flow?.hideNoThanks && (
              <div className="optin-skip-container" style={{ display: "flex" }}>
                <Link
                  className="optin-skip"
                  fontSize="18px"
                  lineHeight="22px"
                  mt={4}
                  onClick={skipOptin}
                  isLoading={isBusy || isSubmitting}
                  style={convertCSStoObject(flow?.cssEditor)[".optin-skip"]}
                >
                  <FormattedMessage {...messages.skip} />
                </Link>
                {/* {isSubmitting || isBusy ? <Spinner ml={1} mt={4}></Spinner> : null} */}
              </div>
            )}
          </MotionFlex>
        </Box>
      </MotionBox>
    </form>
  );
};
